import {
  Card as MUICard,
  CardActionArea,
  Chip,
  Divider,
  Tooltip,
  Typography
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { FolderOutlined, KeyboardArrowRightRounded } from '@material-ui/icons';
import clsx from 'clsx';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Link as RRLink } from 'react-router-dom';
import { RootState } from '../app/store';
import { ProjectDocumentWorkflowMyTask } from '../pages/ProjectDocumentWorkflow/projectDocumentWorkflowSlice';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      margin: theme.spacing(0, 0, 1.5, 0),
      '&:last-child': {
        marginBottom: 0
      },
      width: '100%',
      border: '2px solid',
      borderColor: theme.palette.background.paper,
      borderRadius: 16,
      transition: '0.4s',
      '&:hover, &.Mui-focusVisible': {
        borderColor: theme.palette.primary.main
      }
    },
    container: {
      textDecoration: 'none',
      color: theme.palette.text.primary
    },
    flexProjectName: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      padding: theme.spacing(0, 0.5, 0, 1)
    },
    flexMyTask: {
      display: 'flex',
      alignItems: 'flex-end',
      [theme.breakpoints.down('sm')]: { display: 'block' }
    },
    chip: {
      marginRight: theme.spacing(1),
      [theme.breakpoints.down('xs')]: {
        marginRight: 0
      },
      cursor: 'pointer'
    },
    chipOnDue: {
      borderColor: theme.palette.success.main,
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText
    },
    chipDelay: {
      borderColor: theme.palette.error.main,
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText
    },
    disciplineChip: {
      margin: theme.spacing(0, 1, 0, -0.5),
      cursor: 'pointer',
      fontWeight: 'normal',
      fontSize: 13
    },
    typoDocName: {
      wordBreak: 'break-word',
      lineHeight: '100%'
    },
    divWidth50: {
      width: '50%',
      [theme.breakpoints.down('sm')]: {
        width: '100%'
      }
    },
    divChipWorkflowStateAndDueDate: {
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    textItemRightOnMobile: {
      paddingLeft: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        paddingLeft: theme.spacing(0),
        paddingTop: theme.spacing(0.5),
        textAlign: 'right'
      }
    },
    paddingAll: {
      padding: theme.spacing(1, 1.5)
    },
    projectIcon: {
      marginRight: theme.spacing(0.5)
    }
  })
);

interface DocMyTaskCardProps {
  renderMyTaskDocs: ProjectDocumentWorkflowMyTask[];
}

export default function DocMyTaskCard({
  renderMyTaskDocs
}: DocMyTaskCardProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const { data: disciplines } = useSelector(
    (state: RootState) => state.disciplines
  );

  const today = moment();

  return (
    <div>
      {renderMyTaskDocs.map((renderMyTaskDoc, index) => {
        const project =
          renderMyTaskDoc.projectDocumentRevision.projectDocument.project;
        const documentDiscipline = disciplines.find(
          (docDiscipline) =>
            docDiscipline.id ===
            renderMyTaskDoc.projectDocumentRevision.projectDocument.disciplineId
        );
        const to = renderMyTaskDoc.id
          ? `/approvals/${renderMyTaskDoc.id}`
          : `/document-revisions/${renderMyTaskDoc.projectDocumentRevision.id}`;
        return (
          <MUICard className={classes.card} key={index}>
            <CardActionArea
              className={clsx(classes.container, classes.flexProjectName)}
              component={RRLink}
              to={`/projects/${project.id}`}
            >
              <FolderOutlined
                className={classes.projectIcon}
                fontSize="small"
                color="primary"
              />
              <Typography variant="caption">
                {`${project.projectRegister.projectNo} : ${project.projectRegister.name}`}
              </Typography>
              <KeyboardArrowRightRounded color="action" />
            </CardActionArea>
            <Divider />
            <CardActionArea
              className={clsx(
                classes.container,
                classes.flexMyTask,
                classes.paddingAll
              )}
              component={RRLink}
              to={to}
            >
              <div className={classes.divWidth50}>
                <Typography variant="body1" className={classes.typoDocName}>
                  {documentDiscipline && (
                    <Tooltip title={documentDiscipline?.cfihosName ?? ''} arrow>
                      <Chip
                        className={classes.disciplineChip}
                        component="span"
                        label={documentDiscipline.name}
                        variant="outlined"
                        size="small"
                      />
                    </Tooltip>
                  )}
                  {renderMyTaskDoc.projectDocumentRevision.projectDocument.name}
                </Typography>
                <Typography variant="subtitle2">
                  {renderMyTaskDoc.projectDocumentRevision.projectDocument.no}
                </Typography>
                <Typography variant="caption">
                  {t('docMyTaskCard.purpose')}:{' '}
                  {renderMyTaskDoc.projectDocumentRevision.purpose.abbreviation}
                  {', '}
                  {t('docMyTaskCard.revision')}:{' '}
                  {renderMyTaskDoc.projectDocumentRevision.revision.name}
                </Typography>
              </div>
              <div
                className={clsx(
                  classes.divWidth50,
                  classes.textItemRightOnMobile
                )}
              >
                {renderMyTaskDoc?.workflowStatusId && (
                  <Typography variant="caption" display="block">
                    {t('docMyTaskCard.workflow')}: {renderMyTaskDoc.name}
                  </Typography>
                )}
                <div className={classes.divChipWorkflowStateAndDueDate}>
                  <Chip
                    className={clsx(
                      classes.chip,
                      {
                        [classes.chipDelay]: renderMyTaskDoc.workflowStatusId
                          ? moment(
                              renderMyTaskDoc.currentWorkflowStep.dueDate
                            ) < today
                          : moment(
                              renderMyTaskDoc.projectDocumentRevision
                                .planIssueDate
                            ) < today
                      },
                      {
                        [classes.chipOnDue]: renderMyTaskDoc.workflowStatusId
                          ? moment(
                              renderMyTaskDoc.currentWorkflowStep.dueDate
                            ) >= today
                          : moment(
                              renderMyTaskDoc.projectDocumentRevision
                                .planIssueDate
                            ) >= today
                      }
                    )}
                    label={
                      renderMyTaskDoc.workflowStatusId
                        ? `${t('docMyTaskCard.waitFor')} ${renderMyTaskDoc.currentWorkflowStep.workflowStep.name}`
                        : t('docMyTaskCard.waitForIssue')
                    }
                    size="small"
                  />
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('docMyTaskCard.dueDate')}:{' '}
                    {renderMyTaskDoc?.workflowStatusId
                      ? moment(
                          renderMyTaskDoc.currentWorkflowStep.dueDate
                        ).format('ll')
                      : moment(
                          renderMyTaskDoc.projectDocumentRevision.planIssueDate
                        ).format('ll')}
                  </Typography>
                </div>
              </div>
            </CardActionArea>
          </MUICard>
        );
      })}
    </div>
  );
}
